import { z } from "zod";
import {
  createGetListRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
} from "../util/index.ts";
import { CustomAttributeRecordSchema } from "./CustomAttributeSchemas.ts";
import { EquipmentSchema } from "./EquipmentSchemas.ts";

export const FacilityStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;
export type FacilityStatusType =
  (typeof FacilityStatus)[keyof typeof FacilityStatus];

export const FacilityTypes = {
  battery: "battery",
  compressor_station: "compressor_station",
  custom_treating_station: "custom_treating_station",
  gas_plant: "gas_plant",
  gas_gathering_station: "gas_gathering_station",
  geothermal_facility: "geothermal_facility",
  injection_disposal_facility: "injection/disposal_facility",
  metering_station: "metering_station",
  oil_sands_processing_plant: "oil_sands_processing_plant",
  tank_terminal: "tank_terminal",
  waste_plant: "waste_plant",
  manufacturing_plant: "manufacturing_plant",
  other: "other",
} as const;
export type FacilityTypeType =
  (typeof FacilityTypes)[keyof typeof FacilityTypes];

export const FacilitySchema = z
  .object({
    name: z.string(),
    type: z.nativeEnum(FacilityTypes),
    status: z.enum([FacilityStatus.ACTIVE, FacilityStatus.INACTIVE]),
    latitude: z.number().lte(90).gte(-90),
    longitude: z.number().lte(180).gte(-180),
    custom_attributes: CustomAttributeRecordSchema,
    company_id: z.string().uuid(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("Represents a Facility asset in the Node API");
export type FacilityType = z.infer<typeof FacilitySchema>;

export const FacilityWithEquipmentSchema = FacilitySchema.merge(
  z.object({
    equipment: z.array(z.lazy(() => EquipmentSchema)),
  })
);
export type FacilityWithEquipmentType = z.infer<
  typeof FacilityWithEquipmentSchema
>;

export const FacilityGetListRequestSchema = createGetListRequestSchema(
  FacilitySchema.pick({
    name: true,
    status: true,
    custom_attributes: true,
  })
    .merge(
      z.object({
        "event.id": z.string().uuid(),
        id: z.string().uuid().or(z.array(z.string().uuid())),
      })
    )
    .partial()
);
export type FacilityGetListRequestType = z.infer<
  typeof FacilityGetListRequestSchema
>;
